import { PermissionActions, PermissionModules } from 'types/rolesTypes';

interface SettingsEntry {
  labelKey: string;
  route: string;
  permission: [PermissionModules, PermissionActions];
}

/**
 * Single source-of-truth for every Settings entry.
 */
export const SETTINGS = {
  ORGANIZATIONS: {
    labelKey: 'organizations',
    route: '/organizations',
    permission: [PermissionModules.Organizations, PermissionActions.Read],
  },
  USERS: {
    labelKey: 'users',
    route: '/users',
    permission: [PermissionModules.Users, PermissionActions.Read],
  },
  ROLES: {
    labelKey: 'roles.menu-text',
    route: '/roles',
    permission: [PermissionModules.RoleManagement, PermissionActions.Read],
  },
  PRODUCTS: {
    labelKey: 'products.menu-text',
    route: '/products',
    permission: [PermissionModules.Products, PermissionActions.Read],
  },
  PROCESSES_MAP: {
    labelKey: 'processes-map',
    route: '/process-maps',
    permission: [PermissionModules.ProcessMaps, PermissionActions.Read],
  },
  DATA_TABLES: {
    labelKey: 'data-tables.menu-text',
    route: '/data-tables',
    permission: [PermissionModules.DataTables, PermissionActions.Read],
  },
  DATA_CAPTURE_FORMS: {
    labelKey: 'forms',
    route: '/data-capture',
    permission: [PermissionModules.Forms, PermissionActions.Read],
  },
  REPORTS: {
    labelKey: 'transactions.title',
    route: '/transactions',
    permission: [
      PermissionModules.ReportingAndAnalytics,
      PermissionActions.Read,
    ],
  },
  DATA_SOURCES: {
    labelKey: 'data-sources.title',
    route: '/data-sources',
    permission: [PermissionModules.DataSources, PermissionActions.Read],
  },
  UNITS_OF_MEASURE: {
    labelKey: 'uoms.title',
    route: '/units-of-measure',
    permission: [PermissionModules.SiteManagement, PermissionActions.Read],
  },
  UPLOAD_HISTORY: {
    labelKey: 'upload-history.title',
    route: '/upload-history',
    permission: [PermissionModules.SiteManagement, PermissionActions.Read],
  },
  CONTROL_DOCUMENTS: {
    labelKey: 'control-documents.title',
    route: '/control-documents',
    permission: [PermissionModules.SiteManagement, PermissionActions.Read],
  },
} satisfies Record<string, SettingsEntry>;

type SettingsMap = typeof SETTINGS;
export type SettingKey = keyof SettingsMap;

/**
 * Array consumed by the Settings navigation component.
 */
export const SETTINGS_MENU = (
  Object.entries(SETTINGS) as [SettingKey, SettingsMap[SettingKey]][]
).map(([key, value]) => ({
  key,
  labelKey: value.labelKey,
  route: value.route,
  permission: value.permission,
}));

export type MenuItem = (typeof SETTINGS_MENU)[number]['key'];

export const SETTINGS_ROUTES: Record<string, MenuItem> = Object.fromEntries(
  SETTINGS_MENU.map(item => [item.route, item.key])
);

/**
 * Helper map: setting key ➜ required PermissionModule (read-only).
 */
export const MODULE_BY_SETTING: { [K in SettingKey]: PermissionModules } =
  Object.fromEntries(
    Object.entries(SETTINGS).map(([key, value]) => [key, value.permission[0]])
  ) as { [K in SettingKey]: PermissionModules };

export const DEFAULT_MENU_ROUTE = 'GENERAL' as const;

/**
 * Public union-type used by the UI layer.
 * Keeps MenuItem keys *plus* the default fallback.
 */
export type SettingsMenuItem = MenuItem | typeof DEFAULT_MENU_ROUTE;
